import { Array, BigInt, Console, Effect, Fiber, pipe } from "effect";
import {
  Address,
  DashboardData,
  getChainKeyFromProtocol,
  makeUsd,
  ProtocolKeyService,
  ProtocolStateService,
} from "@silo/types";
import { getUserMarketAddresses } from "./get-user-market-addresses";
import { getRawDashboardMarket } from "./get-raw-dashboard-market";
import { getProtocolState } from "../protocol/get-protocol-state";
import { MerklClient } from "../merkl-client";
import { getDashboardMarket } from "./get-dashboard-market";
import { sortDashboardMarket } from "./utils";
import { getMarketMetaArray } from "./get-market-meta-array";
import { MerklCampaign } from "../merkl-client/types";

export type GetDashboardData = ReturnType<typeof getDashboardData>;
export type GetDashboardDataSuccess = Effect.Effect.Success<GetDashboardData>;
export type GetDashboardDataError = Effect.Effect.Error<GetDashboardData>;
export type GetDashboardDataContext = Effect.Effect.Context<GetDashboardData>;

export function getDashboardData(user: Address) {
  return Effect.gen(function* () {
    yield* Effect.logInfo("Begin 🎬: get dashboard data");

    const protocolKey = yield* ProtocolKeyService;
    const chainKey = getChainKeyFromProtocol(protocolKey);

    const merklCampaignsFiber = yield* Effect.fork(
      MerklClient.getCampaignArray({ activeOnly: true })
    );

    const [marketMetaArray, protocolState] = yield* Effect.all(
      [
        pipe(getUserMarketAddresses(user), Effect.andThen(getMarketMetaArray)),
        getProtocolState(),
      ],
      { concurrency: "unbounded" }
    );

    const rawDashboardMarketArray = yield* pipe(
      Effect.all(
        marketMetaArray.map((marketMeta) =>
          getRawDashboardMarket({ user, marketMeta })
        ),
        { concurrency: "unbounded" }
      ),
      Effect.provideService(ProtocolStateService, protocolState)
    );

    const validRawDashboardMarketArray = pipe(
      rawDashboardMarketArray,
      Array.filter(({ depositBalanceUsd, baseAsset, bridgeAssets }) => {
        /**
         * The graph user market address query may return markets that have no
         * active balance, so we filter those out here
         */
        if (depositBalanceUsd === 0n) {
          return false;
        }

        const assets = pipe(
          [baseAsset, ...bridgeAssets],
          Array.flatMapNullable((asset) => asset)
        );

        return assets.every((asset) => asset.priceNative > 0n);
      })
    );

    const merklCampaigns = yield* pipe(
      Fiber.join(merklCampaignsFiber),
      Effect.tapError(() =>
        Effect.logWarning(
          "Failed ❌: get merkl campaign array - falling back to []"
        )
      ),
      Effect.tapError((error) => Console.error(error)),
      Effect.catchAll(() => Effect.succeed([] as MerklCampaign[]))
    );

    const dashboardMarketArray = yield* Effect.all(
      pipe(
        validRawDashboardMarketArray,
        Array.map((rawDashboardMarket) => {
          const marketMerklCampaigns = merklCampaigns.filter(
            ({ marketAddress }) =>
              marketAddress === rawDashboardMarket.marketAddress
          );

          return getDashboardMarket({
            rawDashboardMarket,
            marketMerklCampaigns,
          });
        })
      ),
      { concurrency: "unbounded" }
    ).pipe(Effect.provideService(ProtocolStateService, protocolState));

    const depositBalanceUsd = pipe(
      dashboardMarketArray,
      Array.map((market) => market.depositBalanceUsd),
      BigInt.sumAll,
      makeUsd
    );

    const debtBalanceUsd = pipe(
      dashboardMarketArray,
      Array.map((market) => market.debtBalanceUsd),
      BigInt.sumAll,
      makeUsd
    );

    const dashboardMarkets = pipe(
      dashboardMarketArray,
      Array.sort(sortDashboardMarket)
    );

    const dashboardData: DashboardData = {
      data: {
        protocolKey,
        chainKey,
        user,
        depositBalanceUsd,
        debtBalanceUsd,
        dashboardMarkets,
      },
    };

    yield* Effect.logInfo("Complete ✅: get dashboard data");

    return dashboardData;
  });
}
